import { useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { FormikTouched, FormikErrors } from 'formik';
import { isEmpty } from 'lodash';

import { useFlashMessenger } from '@/hooks/useFlashMessenger';

import { useUnloadDialog } from '../useUnloadDialog';

export const useScrollWhenError = <T>(
  isSubmitting: boolean,
  errors: FormikErrors<T>,
) => {
  const { flashError } = useFlashMessenger();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const wasSubmitting = useRef(isSubmitting);

  useEffect(() => {
    if (wasSubmitting.current && !isSubmitting && !isEmpty(errors)) {
      const hash = Object.keys(errors)[0];
      navigate(
        {
          pathname: window.location.pathname,
          hash,
        },
        { replace: true },
      );
      flashError(t('ui.error.validation'));
    }
    wasSubmitting.current = isSubmitting;
  }, [isSubmitting, errors, navigate, t, flashError]);
};

export const useUnloadDialogWithFormik = <T>(touched: FormikTouched<T>) => {
  const { hash } = useLocation();
  const isTouched = useMemo(() => {
    return Object.values(touched).includes(true) || !!hash;
  }, [touched, hash]);
  useUnloadDialog(isTouched);
};
